import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { ModalContainer, StyledPaper } from '../../UserRequestsStyles';
import { DEFAULT_CATEGORY, DEFAULT_CATEGORY_LABEL } from '../constants';

export interface AssignAdminModalProps {
  open: boolean;
  onClose: () => void;
  onAssign: (email: string) => Promise<void>;
  categories?: string[];
  currentCategory?: string;
  isCategoriesLoading?: boolean;
  isAssigning: boolean;
}

const schema = yup.object({
  email: yup.string().required('Email is required'),
});

function AssignAdminModal({
  open,
  onClose,
  onAssign,
  categories = [],
  currentCategory,
  isCategoriesLoading = false,
  isAssigning,
}: AssignAdminModalProps) {
  const { control, handleSubmit, reset } = useForm<{ email: string }>({
    resolver: yupResolver(schema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (!open) return;
    if (currentCategory && categories.includes(currentCategory)) {
      reset({ email: currentCategory });
      return;
    }

    if (categories.includes(DEFAULT_CATEGORY)) {
      reset({ email: DEFAULT_CATEGORY });
      return;
    }

    reset({ email: '' });
  }, [categories, currentCategory, open, reset]);

  const submit = async (values: { email: string }) => {
    await onAssign(values.email.trim());
    reset({ email: '' });
  };

  return (
    <ModalContainer open={open} onClose={onClose}>
      <StyledPaper>
        <Box mb={2}>
          <Typography variant="h6">Assign Admin</Typography>
        </Box>
        <form onSubmit={handleSubmit(submit)} noValidate>
          <Box display="flex" flexDirection="column">
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Email bucket"
                  variant="outlined"
                  fullWidth
                  required
                  value={field.value || ''}
                  onChange={event => field.onChange(event.target.value)}
                  disabled={isCategoriesLoading || isAssigning}
                  error={!!error}
                  helperText={error?.message || ' '}
                >
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category === DEFAULT_CATEGORY
                        ? DEFAULT_CATEGORY_LABEL
                        : category}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gridColumnGap={24}>
            <Button onClick={onClose} variant="text" color="primary">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="text"
              color="primary"
              disabled={isAssigning}
            >
              Assign
            </Button>
          </Box>
        </form>
      </StyledPaper>
    </ModalContainer>
  );
}

export default AssignAdminModal;
